import { useEffect, useState } from "react";
import { Pencil, X, Trash2, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Btn, Input, fmtBRL, ExportButtons } from "@/components/kit";

const LABELS = { new: "Novo", qualifying: "Qualificação", proposal: "Proposta", negotiation: "Negociação", won: "Ganho", lost: "Perdido" };
const selectCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none ring-primary/20 focus:ring-4";

function NewOppModal({ stages, onClose, onCreated }) {
  const [form, setForm] = useState({ account_name: "", value: "", stage: "qualifying", next_step: "", next_date: "", owner_id: "" });
  const [accounts, setAccounts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/accounts").then(({ data }) => setAccounts(data || [])).catch(() => {});
    api.get("/employees").then(({ data }) => setEmps((data || []).filter((e) => e.status === "active"))).catch(() => {});
  }, []);
  const save = async () => {
    if (!form.account_name.trim()) return toast.error("Informe o cliente/conta");
    setBusy(true);
    try {
      const payload = {
        account_name: form.account_name.trim(),
        value: form.value === "" ? null : Number(form.value),
        stage: form.stage,
        next_step: form.next_step.trim() || null,
        next_date: form.next_date || null,
        owner_id: form.owner_id || null,
      };
      const { data } = await api.post("/opportunities", payload);
      toast.success(data.created_account ? "Oportunidade criada (conta nova cadastrada)" : "Oportunidade criada");
      onCreated();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-zinc-900/40 p-4 pt-10 sm:pt-16" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="new-opp-modal">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-zinc-900">Nova oportunidade</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">Cliente / Conta</label>
            <Input className="mt-1" list="opp-accounts" value={form.account_name}
              onChange={(e) => setForm({ ...form, account_name: e.target.value })}
              placeholder="Digite o nome (cria a conta se não existir)" data-testid="new-opp-account" />
            <datalist id="opp-accounts">
              {accounts.map((a) => <option key={a.id} value={a.name} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">Valor (R$)</label>
              <Input className="mt-1" type="number" min="0" step="1000" value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Ex.: 250000" data-testid="new-opp-value" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">Estágio</label>
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={selectCls} data-testid="new-opp-stage">
                {stages.map((s) => <option key={s} value={s}>{LABELS[s] || s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Responsável (vendedor)</label>
            <select value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })} className={selectCls} data-testid="new-opp-owner">
              <option value="">Sem responsável</option>
              {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Próximo passo</label>
            <Input className="mt-1" value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} placeholder="Ex.: enviar proposta" data-testid="new-opp-nextstep" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Data do próximo passo</label>
            <Input className="mt-1" type="date" value={form.next_date} onChange={(e) => setForm({ ...form, next_date: e.target.value })} data-testid="new-opp-nextdate" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={busy} data-testid="new-opp-save">{busy ? "Salvando…" : "Criar oportunidade"}</Btn>
        </div>
      </Card>
    </div>
  );
}

function DeleteOppModal({ opp, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/opportunities/${opp.id}`);
      toast.success("Oportunidade excluída");
      onDeleted(opp.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-opp-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-zinc-900">Excluir oportunidade</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <p className="text-sm text-zinc-600">
          Tem certeza que deseja excluir a oportunidade de <b>{opp.account_name}</b>
          {opp.value ? <> ({fmtBRL(opp.value)})</> : null}? A conta e o histórico permanecem — só o card do funil é removido. Esta ação não pode ser desfeita.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={del} disabled={busy} data-testid="delete-opp-confirm"
            className="bg-red-600 text-white hover:bg-red-700">{busy ? "Excluindo…" : "Excluir"}</Btn>
        </div>
      </Card>
    </div>
  );
}

function EditOppModal({ opp, stages, onClose, onSaved }) {
  const [form, setForm] = useState({
    value: opp.value ?? "", stage: opp.stage || "qualifying",
    next_step: opp.next_step || "", next_date: (opp.next_date || "").slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        value: form.value === "" ? null : Number(form.value),
        stage: form.stage,
        next_step: form.next_step,
        next_date: form.next_date || null,
      };
      const { data } = await api.patch(`/opportunities/${opp.id}`, payload);
      toast.success("Oportunidade atualizada");
      onSaved(data.opportunity);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-opp-modal">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-zinc-900">Editar oportunidade · {opp.account_name}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-600">Valor (R$)</label>
            <Input className="mt-1" type="number" min="0" step="1000" value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Ex.: 50000" data-testid="edit-opp-value" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Estágio</label>
            <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="edit-opp-stage"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none ring-primary/20 focus:ring-4">
              {stages.map((s) => <option key={s} value={s}>{LABELS[s] || s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Próximo passo</label>
            <Input className="mt-1" value={form.next_step} onChange={(e) => setForm({ ...form, next_step: e.target.value })} placeholder="Ex.: enviar proposta" data-testid="edit-opp-nextstep" />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Data do próximo passo</label>
            <Input className="mt-1" type="date" value={form.next_date} onChange={(e) => setForm({ ...form, next_date: e.target.value })} data-testid="edit-opp-nextdate" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={save} disabled={busy} data-testid="edit-opp-save">{busy ? "Salvando…" : "Salvar"}</Btn>
        </div>
      </Card>
    </div>
  );
}

export default function Pipeline({ embedded = false }) {
  const [board, setBoard] = useState({});
  const [stages, setStages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState("valor");
  const [collapsed, setCollapsed] = useState(() => new Set());

  const load = () => api.get("/pipeline").then(({ data }) => { setBoard(data.board); setStages(data.stages); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const _pd = (s) => { const v = Date.parse(s); return isNaN(v) ? null : v; };
  const sortItems = (items) => {
    const arr = [...(items || [])];
    if (sort === "prazo") arr.sort((a, b) => (_pd(a.next_date) ?? Infinity) - (_pd(b.next_date) ?? Infinity));
    else if (sort === "nome") arr.sort((a, b) => (a.account_name || "").localeCompare(b.account_name || ""));
    else arr.sort((a, b) => (b.value || 0) - (a.value || 0)); // valor (maior primeiro)
    return arr;
  };
  const toggleCol = (s) => setCollapsed((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {!embedded ? <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Pipeline</h1> : <span />}
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-500">
            Ordenar
            <select data-testid="pipeline-sort" value={sort} onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100">
              <option value="valor">Valor (maior)</option>
              <option value="prazo">Próxima data</option>
              <option value="nome">Cliente (A–Z)</option>
            </select>
          </label>
          <Btn onClick={() => setCreating(true)} data-testid="new-opp-btn"><Plus size={16} className="mr-1" />Nova oportunidade</Btn>
          <ExportButtons basePath="/pipeline/export" name="pipeline" testid="pipeline-export" />
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban">
        {stages.map((s) => (
          <div key={s} className="w-72 shrink-0">
            <button onClick={() => toggleCol(s)} data-testid={`kanban-col-${s}`}
              className="mb-2 flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-zinc-100">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-zinc-700">
                <ChevronDown size={14} className={`text-zinc-400 transition-transform ${collapsed.has(s) ? "-rotate-90" : ""}`} />
                {LABELS[s] || s}
              </span>
              <Badge>{(board[s] || []).length}</Badge>
            </button>
            {!collapsed.has(s) && (
              <div className="space-y-2">
                {sortItems(board[s]).map((o) => (
                  <Card key={o.id} className="group relative p-3 transition-transform hover:-translate-y-0.5" data-testid={`opp-${o.id}`}>
                    <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button onClick={() => setEditing(o)} data-testid={`opp-edit-${o.id}`}
                        className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-indigo-600" title="Editar oportunidade"><Pencil size={13} /></button>
                      <button onClick={() => setDeleting(o)} data-testid={`opp-delete-${o.id}`}
                        className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600" title="Excluir oportunidade"><Trash2 size={13} /></button>
                    </div>
                    <p className="pr-12 text-sm font-medium text-zinc-900">{o.account_name}</p>
                    <p className="mt-1 font-display text-lg font-semibold text-zinc-900">{fmtBRL(o.value)}</p>
                    <p className="mt-1 text-xs text-zinc-500">{o.owner_name}</p>
                    {o.next_step && <p className="mt-2 rounded bg-zinc-100 px-2 py-1 text-xs text-zinc-500">→ {o.next_step}</p>}
                  </Card>
                ))}
                {!(board[s] || []).length && <div className="rounded-lg border border-dashed border-zinc-200 py-6 text-center text-xs text-zinc-400">—</div>}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <EditOppModal opp={editing} stages={stages} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {deleting && (
        <DeleteOppModal opp={deleting} onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }} />
      )}
      {creating && (
        <NewOppModal stages={stages} onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}
